import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { QConfigStatus, useUpdateQConfigStructureNamesMutation } from '@exam-management/graphql';
import { Divider, Flex, Form, Paper, Text } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useImperativeHandle } from 'react';

import { AddQBSButton, UpdateQConfigStructureButton } from '../../components';
import { ADD_QBANK_REQUIRED_PERMISSIONS, EDIT_QCONFIG_REQUIRED_PERMISSIONS } from '../../constants';
import { useQConfigStructureFormContext, type QConfigStructureFormInput } from '../../providers';
import { QConfigStructureList } from '../qconfig-structure-list';

export default function QConfigStructureDetailForm() {
  const { qConfigData, formMethodsRef, publishQConfig } = useQConfig();
  const { structures, status } = qConfigData;
  const formContext = useQConfigStructureFormContext();
  const { showSnackbar } = useSnackbar();

  const [updateQConfigStructureNames, { loading: isUpdateQConfigLoading }] =
    useUpdateQConfigStructureNamesMutation({
      onCompleted: () => {
        showSnackbar({ message: 'QConfig has been saved', variant: 'success' });
        publishQConfig.clearErrors();
      },
      onError: () => {
        showSnackbar({ message: 'Unable to save QConfig', variant: 'error' });
      },
    });

  const onSubmit = async (data: QConfigStructureFormInput) => {
    await updateQConfigStructureNames({
      variables: {
        updateQConfigStructureNamesInput: {
          structures: Object.entries(data).map(([id, name]) => ({
            id,
            name,
          })),
          qConfigId: qConfigData.id,
        },
      },
    });
  };

  const isDraftQConfig = status === QConfigStatus.Draft;
  const hasPermissionForEditingQConfig = useHasPermission(EDIT_QCONFIG_REQUIRED_PERMISSIONS);
  const hasPermissionForAddingQBank = useHasPermission(ADD_QBANK_REQUIRED_PERMISSIONS);

  useImperativeHandle(formMethodsRef, () => ({ formContext, onSubmit }), [formContext]);

  return (
    <Form onSubmit={formContext.handleSubmit(onSubmit)}>
      <Flex
        sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(6), flexDirection: 'column' })}
      >
        <Paper sx={{ width: '100%' }}>
          <Flex sx={{ flexDirection: 'column' }}>
            <Flex sx={(theme) => ({ justifyContent: 'space-between', p: theme.spacing(4, 6) })}>
              <Text variant='h3'>QConfig Structure</Text>
              {isDraftQConfig && hasPermissionForAddingQBank ? <AddQBSButton /> : null}
            </Flex>
            <Divider />
            <QConfigStructureList
              data={structures}
              sx={{
                p: (theme) => theme.spacing(6),
              }}
            />
          </Flex>
        </Paper>
        {isDraftQConfig && hasPermissionForEditingQConfig ? (
          <UpdateQConfigStructureButton type='submit' loading={isUpdateQConfigLoading} />
        ) : null}
      </Flex>
    </Form>
  );
}
